/**
 * Admin Marketing — Sources API. `/api/admin/v2/marketing/sources/*`
 *
 * Covers accounts, outlets, outreach, listening rules, keywords, the relationship
 * funnel, and the publishing providers behind ff-api-external.
 *
 * Every response arrives as `AdminEnvelope<T>` (`{status, data}`), unwrapped via
 * `select` so callers get the payload directly.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiDestinations,
  ApiHandleVerification,
  ApiListenProviders,
  ApiListeningRule,
  ApiListeningRulesHealth,
  ApiMediaOutlet,
  ApiOutreach,
  ApiProvider,
  ApiProviderAccount,
  ApiRelationshipFunnel,
  ApiSourceAccount,
  ApiSourceConnection,
  ApiTrackedKeyword,
  MarketingList,
  MarketingListParams,
} from '@/client/types/marketing/api';
import { MARKETING_BASE, marketingKeys } from './keys';
import { radarKeys } from './radar';

/* --------------------------------------------------------------- accounts */

export const useSourceAccountsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiSourceAccount>>>({
    defaultErrorMessage: 'Could not load source accounts',
    endpoint: `${MARKETING_BASE}/sources/accounts`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.accounts(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useCreateSourceAccountMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiSourceAccount>, AdminEnvelope<ApiSourceAccount>>({
    defaultErrorMessage: 'Could not create the account',
    endpoint: `${MARKETING_BASE}/sources/accounts`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useUpdateSourceAccountMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiSourceAccount> & { said: number },
    AdminEnvelope<ApiSourceAccount>
  >({
    defaultErrorMessage: 'Could not update the account',
    endpoint: (payload) => `${MARKETING_BASE}/sources/accounts/${payload.said}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useDeleteSourceAccountMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ said: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the account',
    endpoint: (payload) => `${MARKETING_BASE}/sources/accounts/${payload.said}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

/* ---------------------------------------------------------------- outlets */

export const useMediaOutletsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiMediaOutlet>>>({
    defaultErrorMessage: 'Could not load media outlets',
    endpoint: `${MARKETING_BASE}/sources/outlets`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.outlets(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useCreateMediaOutletMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiMediaOutlet>, AdminEnvelope<ApiMediaOutlet>>({
    defaultErrorMessage: 'Could not create the outlet',
    endpoint: `${MARKETING_BASE}/sources/outlets`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useUpdateMediaOutletMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiMediaOutlet> & { moid: number },
    AdminEnvelope<ApiMediaOutlet>
  >({
    defaultErrorMessage: 'Could not update the outlet',
    endpoint: (payload) => `${MARKETING_BASE}/sources/outlets/${payload.moid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useDeleteMediaOutletMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ moid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the outlet',
    endpoint: (payload) => `${MARKETING_BASE}/sources/outlets/${payload.moid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

/* --------------------------------------------------------------- outreach */

export const useOutreachQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiOutreach>>>({
    defaultErrorMessage: 'Could not load outreach',
    endpoint: `${MARKETING_BASE}/sources/outreach`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.outreach(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

/**
 * Follow-ups due or overdue.
 *
 * Shorter staleTime than the other lists: this drives the banner the Outreach
 * page opens with, and a stale "3 overdue" is the one number on the page that
 * being wrong actually costs something.
 */
export const useOutreachDueQuery = (withinDays = 7) => {
  const queryFn = useGetQueryFn<AdminEnvelope<{ count: number; items: ApiOutreach[] }>>({
    defaultErrorMessage: 'Could not load due follow-ups',
    endpoint: `${MARKETING_BASE}/sources/outreach/due`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.outreachDue(withinDays),
    queryFn: () => queryFn({ within_days: withinDays }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useCreateOutreachMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiOutreach>, AdminEnvelope<ApiOutreach>>({
    defaultErrorMessage: 'Could not log the outreach',
    endpoint: `${MARKETING_BASE}/sources/outreach`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useUpdateOutreachMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiOutreach> & { moutid: number },
    AdminEnvelope<ApiOutreach>
  >({
    defaultErrorMessage: 'Could not update the outreach entry',
    endpoint: (payload) => `${MARKETING_BASE}/sources/outreach/${payload.moutid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

export const useDeleteOutreachMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ moutid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the outreach entry',
    endpoint: (payload) => `${MARKETING_BASE}/sources/outreach/${payload.moutid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

/* ------------------------------------------------- listening + keywords */

export const useListeningRulesQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiListeningRule>>>({
    defaultErrorMessage: 'Could not load listening rules',
    endpoint: `${MARKETING_BASE}/sources/listening-rules`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.listeningRules(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useCreateListeningRuleMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiListeningRule>, AdminEnvelope<ApiListeningRule>>({
    defaultErrorMessage: 'Could not create the rule',
    endpoint: `${MARKETING_BASE}/sources/listening-rules`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.listeningRulesHealth() });
    },
  });
};

export const useUpdateListeningRuleMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiListeningRule> & { lrid: number },
    AdminEnvelope<ApiListeningRule>
  >({
    defaultErrorMessage: 'Could not update the rule',
    endpoint: (payload) => `${MARKETING_BASE}/sources/listening-rules/${payload.lrid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.listeningRulesHealth() });
    },
  });
};

export const useDeleteListeningRuleMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ lrid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the rule',
    endpoint: (payload) => `${MARKETING_BASE}/sources/listening-rules/${payload.lrid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.listeningRulesHealth() });
    },
  });
};

/**
 * Per-rule ingest health (ok / dry / blind / never_run / disabled).
 *
 * Prefer `lag_minutes` + `interval_minutes` over raw timestamps when rendering
 * "how stale is this watcher".
 */
export const useListeningRulesHealthQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiListeningRulesHealth>>({
    defaultErrorMessage: 'Could not load listening health',
    endpoint: `${MARKETING_BASE}/sources/listening-rules/health`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.listeningRulesHealth(),
    queryFn: () => queryFn(),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useTrackedKeywordsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiTrackedKeyword>>>({
    defaultErrorMessage: 'Could not load tracked keywords',
    endpoint: `${MARKETING_BASE}/sources/keywords`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.keywords(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useCreateTrackedKeywordMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiTrackedKeyword>, AdminEnvelope<ApiTrackedKeyword>>(
    {
      defaultErrorMessage: 'Could not create the tracker',
      endpoint: `${MARKETING_BASE}/sources/keywords`,
      method: 'POST',
    },
  );
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: radarKeys.all });
    },
  });
};

export const useUpdateTrackedKeywordMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiTrackedKeyword> & { tkid: number },
    AdminEnvelope<ApiTrackedKeyword>
  >({
    defaultErrorMessage: 'Could not update the tracker',
    endpoint: (payload) => `${MARKETING_BASE}/sources/keywords/${payload.tkid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: radarKeys.all });
    },
  });
};

export const useDeleteTrackedKeywordMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ tkid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the tracker',
    endpoint: (payload) => `${MARKETING_BASE}/sources/keywords/${payload.tkid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({ queryKey: radarKeys.all });
    },
  });
};

/* ------------------------------------------------------------ aggregates */

export const useRelationshipFunnelQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiRelationshipFunnel>>({
    defaultErrorMessage: 'Could not load the relationship funnel',
    endpoint: `${MARKETING_BASE}/sources/funnel`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.funnel(),
    queryFn: () => queryFn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * Coverage volume per account and per outlet, for the Sources list columns.
 *
 * Keys are stringified ids (JSON has no integer keys) — look up by
 * `String(said)`. One aggregation covers the whole page; do not reach for a
 * per-row count query instead.
 */
export const useSourceCoverageCountsQuery = () => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ accounts: Record<string, number>; outlets: Record<string, number> }>
  >({
    defaultErrorMessage: 'Could not load coverage counts',
    endpoint: `${MARKETING_BASE}/sources/coverage-counts`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.coverageCounts(),
    queryFn: () => queryFn(),
    // Ingest runs every 15 minutes, so these move without anyone on the page.
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** Everything the Studio composer may publish to, in one call. */
export const useDestinationsQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiDestinations>>({
    defaultErrorMessage: 'Could not load destinations',
    endpoint: `${MARKETING_BASE}/sources/destinations`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.destinations(),
    queryFn: () => queryFn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/* -------------------------------------------------------------- providers */

/**
 * Publishing-provider capability matrix.
 *
 * Generated server-side from the adapters that implement them, so it cannot drift
 * from what the code does. The UI must stay capability-driven and never branch on
 * a provider name — adding a sixth aggregator should cost zero React changes.
 */
export const useProvidersQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<{ count: number; providers: ApiProvider[] }>>({
    defaultErrorMessage: 'Could not load publishing providers',
    endpoint: `${MARKETING_BASE}/sources/providers`,
  });
  return useQuery({
    queryKey: marketingKeys.providers.list(),
    queryFn: () => queryFn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * Public-listen capability matrix (Apify / SERP), separate from the publishing
 * rail. Never merge these rows into the publish Providers table — different
 * vendors and different failure modes.
 */
export const useListenProvidersQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiListenProviders>>({
    defaultErrorMessage: 'Could not load listen providers',
    endpoint: `${MARKETING_BASE}/sources/listen-providers`,
  });
  return useQuery({
    queryKey: marketingKeys.providers.listen(),
    queryFn: () => queryFn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useRunConformanceMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { provider: string; connection_id?: string; sample_post_id?: string },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'Could not run the conformance suite',
    endpoint: (payload) =>
      `${MARKETING_BASE}/sources/providers/${payload.provider}/conformance`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.providers.all }),
  });
};

/**
 * Accounts already connected in the provider's own dashboard.
 *
 * Distinct from `useAccountConnectionsQuery`, which lists what we have adopted.
 * A network linked at the vendor is invisible everywhere else until imported,
 * which is the single most common "why can't I see my accounts" cause.
 *
 * Each row carries `adopted` (already ours) and `matched_said` (a registry row
 * exists for this handle+platform but is not yet linked).
 */
export const useProviderAccountsQuery = (provider: string, enabled = true) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ provider: string; count: number; accounts: ApiProviderAccount[] }>
  >({
    defaultErrorMessage: 'Could not list accounts from the provider',
    endpoint: `${MARKETING_BASE}/sources/providers/${provider}/accounts`,
  });
  return useQuery({
    queryKey: marketingKeys.providers.accounts(provider),
    queryFn: () => queryFn(),
    enabled: enabled && !!provider,
    // Reaches the vendor on every call; don't hammer it while a dialog is open.
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    // Missing route / unknown provider is not transient — retrying floods the console.
    retry: (count, err) => {
      const status =
        (err as { response?: { status?: number }; status?: number })?.response?.status ??
        (err as { status?: number })?.status;
      const msg = String((err as { message?: string })?.message ?? '');
      if (status === 404 || /^not found\b/i.test(msg)) return false;
      return count < 2;
    },
    select: (response) => response?.data,
  });
};

/** Adopt provider-side accounts. Partial success is normal — read `failed`. */
export const useImportProviderAccountsMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { provider: string; accounts: Partial<ApiProviderAccount>[] },
    AdminEnvelope<{
      imported: { account_id: string; said: number; handle: string }[];
      failed: { account_id: string; error: string }[];
      imported_count: number;
      failed_count: number;
    }>
  >({
    defaultErrorMessage: 'Could not import the accounts',
    endpoint: (payload) =>
      `${MARKETING_BASE}/sources/providers/${payload.provider}/accounts/import`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      queryClient.invalidateQueries({
        queryKey: marketingKeys.providers.accounts(variables.provider),
      });
    },
  });
};

/* ------------------------------------------------------- connect + verify */

/**
 * Check a handle exists publicly.
 *
 * Resolves to `exists | not_found | indeterminate`. Callers must render
 * `indeterminate` as "couldn't check" — never as a dead handle.
 */
export const useVerifyHandleMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { said: number; provider?: string },
    AdminEnvelope<ApiHandleVerification>
  >({
    defaultErrorMessage: 'Could not verify the handle',
    endpoint: (payload) =>
      `${MARKETING_BASE}/sources/accounts/${payload.said}/verify-handle`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all }),
  });
};

/** Start OAuth linking. Returns an authorize URL for the browser to visit. */
export const useConnectAccountMutation = () => {
  const mutationFn = useGetMutationFn<
    { said: number; provider?: string; redirect_uri: string; return_to?: string },
    AdminEnvelope<{ authorize_url: string; state: string; provider: string }>
  >({
    defaultErrorMessage: 'Could not start the connection',
    endpoint: (payload) => `${MARKETING_BASE}/sources/accounts/${payload.said}/connect`,
    method: 'POST',
  });
  // Intentionally no cache invalidation: nothing has been connected yet. The
  // connection appears only after the provider redirects back.
  return useMutation({ mutationFn });
};

export const useAccountConnectionsQuery = (said: number, enabled = true) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ said: number; count: number; connections: ApiSourceConnection[] }>
  >({
    defaultErrorMessage: 'Could not load connections',
    endpoint: `${MARKETING_BASE}/sources/accounts/${said}/connections`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.accountConnections(said),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(said),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** Is our token still live and scoped? Distinct from the handle check. */
export const useVerifyConnectionMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { said: number; mscid: number },
    AdminEnvelope<{ state: string; detail: string | null }>
  >({
    defaultErrorMessage: 'Could not verify the connection',
    endpoint: (payload) =>
      `${MARKETING_BASE}/sources/accounts/${payload.said}/connections/${payload.mscid}/verify`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: marketingKeys.sources.accountConnections(variables.said),
      }),
  });
};
